import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import SellerLayout from "@/components/SellerLayout";
import { PageHeader, DataTable, EmptyState, ConfirmDialog } from "@/components/ui-system";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Scale, AlertTriangle, Check, MessageSquareWarning, Info } from "lucide-react";
import { formatToIST } from "@/utils/dateUtils";
import { useLocalStorageState } from "@/hooks/useLocalStorageState";
import { can } from "@/lib/permissions";

const PAGE_SIZE = 50;
const inr = (n) => `₹${Number(n ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const kg = (n) => (n == null ? "—" : `${Number(n)} kg`);
const dims = (d) => (d && d.length_cm ? `${d.length_cm}×${d.breadth_cm}×${d.height_cm}` : "—");
const errDetail = (e, fb) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return e?.response?.data?.message || fb;
};

const TABS = [
  { id: "action_required", label: "Action Required" },
  { id: "disputed", label: "Disputed" },
  { id: "accepted", label: "Accepted" },
  { id: "resolved", label: "Resolved" },
  { id: "all", label: "All" },
];

const WD_STATUS = {
  ACTION_REQUIRED: { label: "Action required", cls: "bg-amber-100 text-amber-800" },
  DISPUTED: { label: "Disputed", cls: "bg-blue-100 text-blue-800" },
  ACCEPTED: { label: "Accepted", cls: "bg-slate-100 text-slate-700" },
  AUTO_ACCEPTED: { label: "Auto-accepted", cls: "bg-slate-100 text-slate-700" },
  RESOLVED_UPHELD: { label: "Upheld — charged", cls: "bg-red-100 text-red-700" },
  RESOLVED_REVERSED: { label: "Reversed", cls: "bg-green-100 text-green-700" },
};

const WdStatus = ({ s }) => {
  const c = WD_STATUS[s] || { label: s, cls: "bg-slate-100 text-slate-700" };
  return <Badge className={`${c.cls} border-0`}>{c.label}</Badge>;
};

const daysLeft = (deadline) => {
  if (!deadline) return null;
  const ms = new Date(deadline).getTime() - Date.now();
  return Math.ceil(ms / 86400000);
};

const B2CWeightDiscrepancy = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const canAct = can(user, "b2c.weight_discrepancy:act");

  const [activeTab, setActiveTab] = useLocalStorageState("b2c-wd:tab", "action_required");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [counts, setCounts] = useState({});
  const [windowDays, setWindowDays] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notEnabled, setNotEnabled] = useState(false);

  const [acceptTarget, setAcceptTarget] = useState(null);
  const [accepting, setAccepting] = useState(false);

  const [disputeTarget, setDisputeTarget] = useState(null);
  const [dispute, setDispute] = useState({ category: "", remarks: "", product_url: "" });
  const [files, setFiles] = useState([]);
  const [disputing, setDisputing] = useState(false);

  const tabGroup = (TABS.find((t) => t.id === activeTab) || TABS[0]).id;

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (tabGroup && tabGroup !== "all") params.status_group = tabGroup;
      else if (tabGroup === "all") params.status_group = "all";
      const { data: res } = await axios.get(`${API}/b2c/seller/weight-discrepancies`, { params });
      setData(res);
      if (res.counts) setCounts(res.counts);
      if (res.dispute_window_days != null) setWindowDays(res.dispute_window_days);
      setNotEnabled(false);
    } catch (e) {
      if (e?.response?.status === 422) setNotEnabled(true);
      else toast.error(errDetail(e, "Failed to load weight discrepancies"));
    } finally {
      setLoading(false);
    }
  }, [page, tabGroup]);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { setPage(1); }, [activeTab]);

  const doAccept = async () => {
    if (!acceptTarget) return;
    setAccepting(true);
    try {
      await axios.post(`${API}/b2c/seller/weight-discrepancies/${acceptTarget.id}/accept`);
      toast.success(`Accepted — ${inr(acceptTarget.charge_amount)} debited from your wallet.`);
      setAcceptTarget(null);
      fetchList();
    } catch (e) {
      toast.error(errDetail(e, "Could not accept the discrepancy"));
    } finally {
      setAccepting(false);
    }
  };

  const openDispute = (row) => {
    setDisputeTarget(row);
    setDispute({ category: "", remarks: "", product_url: "" });
    setFiles([]);
  };

  const submitDispute = async () => {
    if (!disputeTarget) return;
    if (!dispute.category.trim() || !dispute.remarks.trim()) {
      toast.error("Product category and remarks are required.");
      return;
    }
    setDisputing(true);
    try {
      const fd = new FormData();
      fd.append("category", dispute.category.trim());
      fd.append("remarks", dispute.remarks.trim());
      if (dispute.product_url.trim()) fd.append("product_url", dispute.product_url.trim());
      files.forEach((f) => fd.append("evidence", f));
      await axios.post(`${API}/b2c/seller/weight-discrepancies/${disputeTarget.id}/dispute`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Dispute raised — our team will review your evidence. No charge while it's reviewed.");
      setDisputeTarget(null);
      fetchList();
    } catch (e) {
      toast.error(errDetail(e, "Could not raise the dispute"));
    } finally {
      setDisputing(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));
  const tabCount = (id) => (id === "all" ? counts.all ?? data.total : counts[id]);

  const columns = [
    {
      key: "wd", header: "Discrepancy", primary: true,
      cell: (r) => (
        <div className="text-sm">
          <div className="font-mono text-[13px] text-slate-700">{r.wd_number}</div>
          <button onClick={() => navigate(`/seller/b2c/orders/${r.order_id}`)} className="text-xs text-brand hover:underline">{r.order_number}</button>
        </div>
      ),
    },
    { key: "awb", header: "AWB", hideOnMobile: true, cell: (r) => <span className="text-sm font-mono text-slate-600">{r.awb || "—"}</span> },
    {
      key: "declared", header: "Declared", align: "right",
      cell: (r) => (
        <div className="text-sm text-right">
          <div className="tabular-nums font-medium text-slate-700">{kg(r.declared_weight_kg)}</div>
          <div className="text-xs text-slate-400">{dims(r.declared_dims)}</div>
        </div>
      ),
    },
    {
      key: "applied", header: "Applied (courier)", align: "right",
      cell: (r) => (
        <div className="text-sm text-right">
          <div className="tabular-nums font-semibold text-slate-900">{kg(r.applied_weight_kg)}</div>
          <div className="text-xs text-amber-600">+{Number(r.extra_weight_kg)} kg</div>
        </div>
      ),
    },
    { key: "charge", header: "Charge", align: "right", cell: (r) => <span className="text-sm font-semibold tabular-nums text-slate-800">{inr(r.charge_amount)}</span> },
    {
      key: "status", header: "Status",
      cell: (r) => {
        const dl = r.status === "ACTION_REQUIRED" ? daysLeft(r.action_deadline) : null;
        return (
          <div className="space-y-1">
            <WdStatus s={r.status} />
            {dl != null && (
              <div className={`text-xs font-medium ${dl <= 1 ? "text-red-600" : "text-slate-500"}`}>
                {dl < 0 ? "auto-accepting…" : dl === 0 ? "due today" : `${dl}d left`}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "actions", header: "Actions", isActions: true,
      cell: (r) =>
        r.status === "ACTION_REQUIRED" && canAct ? (
          <div className="flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
            <Button size="sm" variant="outline" onClick={() => setAcceptTarget(r)} data-testid={`wd-accept-${r.id}`}>
              <Check className="w-3.5 h-3.5 mr-1" /> Accept
            </Button>
            <Button size="sm" onClick={() => openDispute(r)} data-testid={`wd-dispute-${r.id}`}>
              <MessageSquareWarning className="w-3.5 h-3.5 mr-1" /> Dispute
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <SellerLayout user={user} onLogout={onLogout}>
      <div className="space-y-6 w-full">
        <PageHeader
          title="Weight Discrepancies"
          subtitle="Courier re-weigh charges — accept, or dispute with proof before the window closes"
        />

        {notEnabled ? (
          <Card className="border-0 shadow-sm"><CardContent className="p-0">
            <EmptyState icon={Scale} title="B2C is not enabled yet" description="Your account doesn't have a B2C plan configured. Ask your account manager." />
          </CardContent></Card>
        ) : (
          <>
            <div className="flex items-start gap-3 rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600">
              <Info className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
              <span>When a courier re-weighs a parcel heavier than you declared, the extra freight shows up here. <strong>Accept</strong> to pay it, or <strong>Dispute</strong> with a weighing-scale + ruler photo{windowDays != null && <> within <strong>{windowDays} day{windowDays === 1 ? "" : "s"}</strong></>}. If you don't act in time, it is auto-accepted and charged.</span>
            </div>

            <div className="flex gap-2 border-b border-slate-200 bg-white px-2 sm:px-6 rounded-t-lg shadow-sm overflow-x-auto">
              {TABS.map((tab) => {
                const c = tabCount(tab.id);
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    data-testid={`wd-tab-${tab.id}`}
                    className={`px-3 sm:px-5 py-3 text-xs sm:text-sm font-medium transition-colors border-b-2 whitespace-nowrap flex-shrink-0 ${
                      activeTab === tab.id ? "border-brand text-brand" : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"
                    }`}
                  >
                    {tab.label}{c != null && <span className="font-bold text-brand"> ({c})</span>}
                  </button>
                );
              })}
            </div>

            <DataTable
              columns={columns}
              data={data.items}
              rowKey={(r) => r.id}
              loading={loading}
              empty={<EmptyState icon={Scale} title="No weight discrepancies 🎉" description="If a courier re-weighs a parcel heavier than you declared, the extra charge appears here to accept or dispute." />}
              pagination={totalPages > 1 ? { page, pages: totalPages, total: data.total, pageSize: PAGE_SIZE, onPageChange: setPage } : undefined}
            />
          </>
        )}
      </div>

      {/* Accept confirm */}
      <ConfirmDialog
        open={!!acceptTarget}
        onOpenChange={(v) => !v && setAcceptTarget(null)}
        title={`Accept ${acceptTarget?.wd_number || ""}?`}
        description={`This accepts the courier's re-weigh of ${kg(acceptTarget?.applied_weight_kg)} and debits ${inr(acceptTarget?.charge_amount)} from your wallet. This can't be undone.`}
        confirmLabel={`Accept & pay ${inr(acceptTarget?.charge_amount)}`}
        loading={accepting}
        onConfirm={doAccept}
      />

      {/* Dispute modal */}
      <Dialog open={!!disputeTarget} onOpenChange={(v) => !v && setDisputeTarget(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Dispute {disputeTarget?.wd_number}</DialogTitle></DialogHeader>
          {disputeTarget && (
            <div className="space-y-4">
              <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>Declared {kg(disputeTarget.declared_weight_kg)} ({dims(disputeTarget.declared_dims)}) · courier charged {kg(disputeTarget.applied_weight_kg)}. Upload a photo of the parcel on a weighing scale with a ruler to win the dispute.</span>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="wd-cat" className="text-xs text-slate-600">Product category <span className="text-red-500">*</span></Label>
                <Input id="wd-cat" value={dispute.category} onChange={(e) => setDispute({ ...dispute, category: e.target.value })} placeholder="e.g. Apparel, Electronics" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="wd-remarks" className="text-xs text-slate-600">Remarks <span className="text-red-500">*</span></Label>
                <Textarea id="wd-remarks" rows={2} value={dispute.remarks} onChange={(e) => setDispute({ ...dispute, remarks: e.target.value })} placeholder="Why the courier's weight is wrong" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="wd-url" className="text-xs text-slate-600">Product URL (optional)</Label>
                <Input id="wd-url" value={dispute.product_url} onChange={(e) => setDispute({ ...dispute, product_url: e.target.value })} placeholder="https://…" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Evidence images (scale + ruler)</Label>
                <input
                  type="file" accept="image/*,application/pdf" multiple
                  onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 5))}
                  className="block w-full text-sm file:mr-3 file:rounded file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm"
                  data-testid="wd-evidence"
                />
                {files.length > 0 && <p className="text-xs text-slate-500">{files.length} file(s) selected</p>}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisputeTarget(null)} disabled={disputing}>Cancel</Button>
            <Button onClick={submitDispute} disabled={disputing}>{disputing ? "Submitting…" : "Raise dispute"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SellerLayout>
  );
};

export default B2CWeightDiscrepancy;
